import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableActions from "./table-actions";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import { Box, TablePagination, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetStockCountsList } from "src/api/inventory/counts";
import { InventoryPermissions } from "src/constants/permissions";
import { specificBranchHasPermission } from "src/utils";

const AllTable = () => {
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const { t } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const tableHeadText = [
    generalT("reference"),
    generalT("branch"),
    generalT("status"),
    t("recipeCost"),
    t("consumptionCost"),
    t("wastageCost"),
    t("inventoryTotal"),
    generalT("cogs"),
    t("byEndOf"),
    generalT("createdBy"),
    generalT("createdAt"),
    generalT("actions"),
  ];

  // APIS
  const { data, isFetching, refetch } = useGetStockCountsList({
    page: pageNumber + 1,
  });

  const emptyDataArr = data?.data.length === 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.meta?.total as number);
    }
  }, [data]);

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {tableHeadText.map((item) => (
                <TableCell
                  key={item}
                  align="center"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    color: "#475467",
                    bgcolor: "#F9FAFB",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isFetching ? (
            <TableLoadingSkeleton rowsLength={8} cellsLength={12} />
          ) : (
            <TableBody>
              {data?.data.map((row: any) => {
                const { recipe_cost, consumtion_cost, wastage_cost } = row;
                const confirmedPermission = specificBranchHasPermission(
                  InventoryPermissions.confirmStocktaking,
                  row.branch_id,
                );
                const hasCogsRef = !!row.cogs_reference;
                const hasCogsAmount =
                  row.status === "confirmed" && row.cogs_amount !== null;

                return (
                  <TableRow
                    key={row.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell align="center">{row.reference}</TableCell>
                    <TableCell align="center">{row.branch_name}</TableCell>
                    <TableCell align="center">
                      <Box
                        color={
                          row.status === "draft"
                            ? "#1D2939"
                            : row.status === "sent"
                              ? "#1A1AE6"
                              : "#085D3A"
                        }
                        border={"1px solid"}
                        borderColor={
                          row.status === "draft"
                            ? "#D0D5DD"
                            : row.status === "sent"
                              ? "#9999F4"
                              : "#47CD89"
                        }
                        bgcolor={
                          row.status === "draft"
                            ? "#F2F4F7"
                            : row.status === "sent"
                              ? "#E6E6FD"
                              : "#ECFDF3"
                        }
                        borderRadius={"50px"}
                        width={"fit-content"}
                        p={"4px 8px"}
                        mx={"auto"}
                      >
                        {t(`${row.status}`)}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        // color={
                        //   recipe_cost < 0
                        //     ? "#F04438"
                        //     : recipe_cost > 0
                        //     ? "#00BF58"
                        //     : ""
                        // }
                        fontSize={"14px"}
                        lineHeight={"20px"}
                      >
                        {row.status === "draft" || !confirmedPermission
                          ? "--"
                          : `SAR ${recipe_cost.toFixed(3)}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontSize={"14px"} lineHeight={"20px"}>
                        {row.status === "draft" || !confirmedPermission
                          ? "--"
                          : `SAR ${consumtion_cost.toFixed(3)}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontSize={"14px"} lineHeight={"20px"}>
                        {row.status === "draft" || !confirmedPermission
                          ? "--"
                          : `SAR ${wastage_cost.toFixed(3)}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {row.status === "draft" || !confirmedPermission
                        ? "--"
                        : row.total.toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        color={
                          hasCogsAmount || hasCogsRef ? "#085D3A" : "#1D2939"
                        }
                        border={"1px solid"}
                        borderColor={
                          hasCogsAmount || hasCogsRef ? "#47CD89" : "#D0D5DD"
                        }
                        bgcolor={
                          hasCogsAmount || hasCogsRef ? "#ECFDF3" : "#FEF9C3"
                        }
                        borderRadius={"50px"}
                        width={"fit-content"}
                        p={"4px 8px"}
                        mx={"auto"}
                      >
                        {hasCogsAmount
                          ? `SAR ${row.cogs_amount?.toFixed(3)}`
                          : generalT(hasCogsRef ? "done" : "pending")}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{row.date}</TableCell>
                    <TableCell align="center">{row.created_by}</TableCell>
                    <TableCell align="center">{row.created_at}</TableCell>
                    <TableCell align="center">
                      <TableActions row={row} refetch={refetch} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
        {emptyDataArr && <NoData />}
      </TableContainer>
      <TablePagination
        sx={{ mt: 1 }}
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />
    </Box>
  );
};
export default AllTable;
